/**
 * Luhn algorithm implementation for credit card validation
 * https://en.wikipedia.org/wiki/Luhn_algorithm
 */

/**
 * Validates a credit card number using the Luhn algorithm
 * @param cardNumber - Card number string (can include spaces/dashes)
 * @returns true if valid, false otherwise
 */
export function luhnCheck(cardNumber: string): boolean {
  if (!cardNumber) return false;

  // Remove all non-digit characters
  const digits = cardNumber.replace(/\D/g, '');

  // Card number must be between 13 and 19 digits
  if (digits.length < 13 || digits.length > 19) {
    return false;
  }

  // Convert to array of numbers
  const digitArray = digits.split('').map(Number);

  // Luhn algorithm
  let sum = 0;
  let isSecond = false;

  // Process digits from right to left
  for (let i = digitArray.length - 1; i >= 0; i--) {
    let digit = digitArray[i];

    if (digit === undefined) continue;

    if (isSecond) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isSecond = !isSecond;
  }

  return sum % 10 === 0;
}

/**
 * Gets the last 4 digits of a card number
 * @param cardNumber - Card number string
 * @returns Last 4 digits or empty string if invalid
 */
export function getLast4Digits(cardNumber: string): string {
  const digits = cardNumber.replace(/\D/g, '');
  if (digits.length < 4) return '';
  return digits.slice(-4);
}

/**
 * Masks a card number showing only the last 4 digits
 * @param cardNumber - Card number string
 * @returns Masked card number (e.g., "************1234")
 */
export function maskCardNumber(cardNumber: string): string {
  const digits = cardNumber.replace(/\D/g, '');
  if (digits.length < 4) return '*'.repeat(digits.length);
  return '*'.repeat(digits.length - 4) + digits.slice(-4);
}
